import React, { createContext, useCallback, useContext, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import PaywallModal from "@/components/PaywallModal";
import SignInModal from "@/components/SignInModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PLANS = [
  {
    key: "guide", name: "Guide", useCase: "Find your game",
    priceMonthly: "$1.99", priceYearly: "$19.99", yearlySavings: "Save 16%",
    tagline: "The schedule, plus the story on every matchup.",
    features: [
      "Full sports schedule + where to watch",
      "Game Insights on every game",
      "Favorites & Up Next",
      "Subscription memory for your streamers",
      "Add-to-calendar (.ics) export",
    ],
  },
  {
    key: "fantasy", name: "Fantasy", useCase: "Find your fantasy",
    priceMonthly: "$7.99", priceYearly: "$59.99", yearlySavings: "Save 37%",
    tagline: "Everything in Guide, plus start/sit for every slate.",
    features: [
      "Everything in Guide",
      "Fantasy Insights — START / FADE / INJURY WATCH per game",
      "Reads player-prop markets, ESPN injuries + weather",
      "NFL + NBA + MLB + NHL",
    ],
  },
  {
    key: "pro", name: "Pro", useCase: "Find your bet",
    priceMonthly: "$12.99", priceYearly: "$99.99", yearlySavings: "Save 36%",
    tagline: "Everything in Guide and Fantasy, plus the sharp side of the line.",
    features: [
      "Everything in Guide and Fantasy",
      "Betting Insights — angles, trends, line context",
      "Parlay tool with Kelly sizing",
      "Player props + shareable parlay slips",
    ],
  },
];

const RANK = { guide: 1, fantasy: 2, pro: 3 };
export const FEATURES = {
  game: { label: "Game Insights", plan: "guide" },
  fantasy: { label: "Fantasy Insights", plan: "fantasy" },
  betting: { label: "Betting Insights", plan: "pro" },
  favorites: { label: "Favorites & Up Next", plan: "guide" },
  streamers: { label: "Saved streamers", plan: "guide" },
};
const PLAN_NAME = { guide: "Guide", fantasy: "Fantasy", pro: "Pro" };

export function unlockLine(feature) {
  const f = FEATURES[feature];
  return f ? `${f.label} unlock with ${PLAN_NAME[f.plan]}` : null;
}

export function canUse(user, feature) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return !!user.plan && RANK[user.plan] >= RANK[FEATURES[feature].plan];
}

const PaywallContext = createContext(null);

export function PaywallProvider({ children }) {
  const { user, status } = useAuth();
  const [paywall, setPaywall] = useState(null);
  const [signIn, setSignIn] = useState(null);
  const [pending, setPending] = useState("");

  const openPaywall = useCallback((feature = null) => setPaywall({ feature }), []);
  const openSignIn = useCallback((mode = "signup") => setSignIn(mode), []);

  const startCheckout = useCallback(async (plan, interval) => {
    if (status !== "in" || !user) { setSignIn("signup"); return; }
    setPending(`${plan}_${interval}`);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        plan, interval, origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout.", { description: formatApiError(e) });
      setPending("");
    }
  }, [status, user]);

  return (
    <PaywallContext.Provider value={{ openPaywall, openSignIn, startCheckout, pending }}>
      {children}
      <PaywallModal open={!!paywall} feature={paywall?.feature} onClose={() => setPaywall(null)} />
      <SignInModal key={signIn || "closed"} open={!!signIn} defaultMode={signIn || "signup"} onClose={() => setSignIn(null)} redirectTo={null} />
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const ctx = useContext(PaywallContext);
  if (!ctx) throw new Error("usePaywall must be used within PaywallProvider");
  return ctx;
}
